<script setup lang="ts">
import type { FormattedCity } from '@@/server/types/city'
import ruLayoutConvert from 'convert-layout'
import CyrillicToTranslit from 'cyrillic-to-translit-js'

type Props = {
  activeSearch?: boolean
  searchQuery?: string
  formattedCitiesList: FormattedCity[]
}

const props = withDefaults(defineProps<Props>(), {
  activeSearch: false,
  searchQuery: '',
})

const cityTitle = 'Перейти на сайт Farfor в городе '

const highlightMatchedLetters = (cityName: string) => {
  // находим и выделяем совпадающие буквы
  if (!props.searchQuery) return cityName

  const name: string = cityName.toLowerCase()
  let searchQueryString: string

  if (name.includes(props.searchQuery)) {
    searchQueryString = props.searchQuery
  } else if (name.includes(ruLayoutConvert.ru.fromEn(props.searchQuery))) {
    searchQueryString = ruLayoutConvert.ru.fromEn(props.searchQuery)
  } else {
    searchQueryString = CyrillicToTranslit().reverse(props.searchQuery)
  }

  return cityName.replace(
    new RegExp(searchQueryString, 'gi'),
    (match) =>
      `<span class="cities-list-container__highlighted-text">${match}</span>`,
  )
}
const itemBeforeLeave = (element: HTMLElement) => {
  element.style.height = element.clientHeight + 'px'
}

const onClickCityLink = (link: string | undefined) => {
  if (!link) return

  const route = useRoute()
  const chatId = route.query.telegram_chat_id as string | undefined
  const vkUserId = route.query.vk_user_id as string | undefined
  let query = ''

  // Если есть telegram_chat_id, добавляем его в ссылку
  if (chatId) {
    query = `?telegram_chat_id=${chatId}`
    // Отправляем метрику "выбор города TG miniapps"
    const { YM_ID } = useRuntimeConfig().public
    window?.ym(YM_ID, 'reachGoal', 'twa_city_selected')
  }

  // Если есть vk_user_id, добавляем его в ссылку
  if (vkUserId) {
    query = `?vk_user_id=${vkUserId}`
    // Отправляем метрику "выбор города TG miniapps"
    const { YM_ID } = useRuntimeConfig().public
    window?.ym(YM_ID, 'reachGoal', 'vkwa_city_selected')
  }

  window.location.href = `${link}${query}`
}
</script>

<template>
  <div
    id="citiesList"
    class="cities-list-container"
    :class="{ 'cities-list-container--active': activeSearch }"
    data-test-id="cities-list-container"
  >
    <transition-group
      name="collapse-fade"
      tag="div"
      class="cities-list-container__list anim-slide-fade-up"
      :class="{ 'anim-slide-fade-up--active': activeSearch }"
      data-test-id="cities-list"
      @before-leave="itemBeforeLeave($event as HTMLElement)"
    >
      <div
        v-for="city in formattedCitiesList"
        :key="city.domain_prefix"
        class="cities-list-container__item"
        data-test-id="city-item"
      >
        <span
          v-if="city?.firstLetter"
          class="cities-list-container__first-letter"
          data-test-id="city-first-letter"
        >
          {{ city.firstLetter }}
        </span>

        <a
          class="cities-list-container__link"
          :href="city.cityUrl"
          :title="cityTitle + city.visible_name"
          data-test-id="city-link"
          @click.prevent="onClickCityLink(city.cityUrl)"
          v-html="highlightMatchedLetters(city.visible_name)"
        />
      </div>
    </transition-group>
  </div>
</template>

<style scoped lang="scss">
.cities-list-container {
  height: 0;
  margin-top: 0;
  overflow: hidden;
  opacity: 0;
  transition: 0.1s;

  &--active {
    height: auto;
    margin-top: 10px;
    opacity: 1;

    @include media($bp-tablet) {
      height: 540px;
    }
  }

  &__list {
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    width: 100%;
    max-width: 100%;
    height: auto;

    @include media($bp-desktop-mini) {
      width: max-content;
      height: 100%;
    }
  }

  &__item {
    position: relative;
    width: 100%;
    padding-left: 20px;
    font-size: 14px;
    line-height: 30px;

    @include media($bp-tablet) {
      width: calc(100% / 4);
    }

    @include media($bp-desktop-mini) {
      width: 170px;
    }
  }

  &__first-letter {
    position: absolute;
    left: 0;
    font-size: 14px;
    font-weight: 500;
    color: var(--ui-color-primary);
  }

  &__link {
    font-size: 14px;
    font-weight: 500;
    line-height: 30px;
    color: var(--ui-color-white);
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  :deep(.cities-list-container__highlighted-text) {
    color: var(--ui-color-primary);
  }
}
</style>
